export type SafetyDocStatus = 'DRAFT' | 'APPROVED' | string;

export type SafetyCheckResult = 'OK' | 'NG' | 'NA' | '';

export interface SafetyPpeIssuanceAttachment {
  attachmentId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface SafetyCheckSheetAttachment {
  attachmentId: number;
  fileName: string;
  fileExt?: string | null;
  fileSize?: number | null;
}

export interface SafetyPpeItem {
  id: string;
  equipmentName: string;
  qty: string;
  issueDate: string;
  employeeName: string;
  /** 수령자 부서 (조직 선택 시) */
  departmentName?: string;
  userId?: number | null;
  departmentId?: number | null;
  /** true면 조직 선택이 아닌 직접 입력 */
  manualEntry?: boolean;
  remark: string;
  /** 수령 서명 (data URL, JPEG/PNG) */
  signatureData?: string;
}

export interface SafetyCheckItem {
  id: string;
  checkItem: string;
  method: string;
  result: SafetyCheckResult | string;
  finding: string;
  remark: string;
}

export interface SafetyPpeIssuance {
  issuanceId: number;
  contractId?: number | null;
  planYear?: number | null;
  docNo?: string | null;
  title?: string | null;
  itemsJson?: string | null;
  status?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  attachmentCount?: number | null;
  attachments?: SafetyPpeIssuanceAttachment[] | null;
  remark?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SafetyPpeIssuancePayload {
  planYear: number;
  title: string;
  itemsJson?: string | null;
  remark?: string | null;
  contractId?: number | null;
}

export interface SafetyCheckSheet {
  sheetId: number;
  contractId?: number | null;
  sheetNo?: string | null;
  title?: string | null;
  checkDate?: string | null;
  areaName?: string | null;
  checkerName?: string | null;
  itemsJson?: string | null;
  status?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  attachmentCount?: number | null;
  attachments?: SafetyCheckSheetAttachment[] | null;
  remark?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SafetyCheckSheetPayload {
  title: string;
  checkDate?: string | null;
  areaName?: string | null;
  checkerName?: string | null;
  itemsJson?: string | null;
  remark?: string | null;
  contractId?: number | null;
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultSafetyPpeItems(): SafetyPpeItem[] {
  return [
    {
      id: newId(),
      equipmentName: '',
      qty: '',
      issueDate: '',
      employeeName: '',
      departmentName: '',
      userId: null,
      departmentId: null,
      manualEntry: false,
      remark: '',
      signatureData: '',
    },
  ];
}

export function defaultSafetyCheckItems(): SafetyCheckItem[] {
  return [
    {
      id: newId(),
      checkItem: '',
      method: '',
      result: '',
      finding: '',
      remark: '',
    },
  ];
}

export function parseSafetyPpeItems(json?: string | null): SafetyPpeItem[] {
  if (!json?.trim()) return defaultSafetyPpeItems();
  try {
    const parsed = JSON.parse(json) as SafetyPpeItem[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultSafetyPpeItems();
    return parsed.map((row) => ({
      id: row.id || newId(),
      equipmentName: row.equipmentName || '',
      qty: row.qty != null ? String(row.qty) : '',
      issueDate: row.issueDate || '',
      employeeName: row.employeeName || '',
      departmentName: row.departmentName || '',
      userId: row.userId != null ? Number(row.userId) : null,
      departmentId: row.departmentId != null ? Number(row.departmentId) : null,
      manualEntry:
        Boolean(row.manualEntry) ||
        (row.userId == null && Boolean(row.employeeName)),
      remark: row.remark || '',
      signatureData: row.signatureData || '',
    }));
  } catch {
    return defaultSafetyPpeItems();
  }
}

export function parseSafetyCheckItems(json?: string | null): SafetyCheckItem[] {
  if (!json?.trim()) return defaultSafetyCheckItems();
  try {
    const parsed = JSON.parse(json) as SafetyCheckItem[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultSafetyCheckItems();
    return parsed.map((row) => ({
      id: row.id || newId(),
      checkItem: row.checkItem || '',
      method: row.method || '',
      result: row.result || '',
      finding: row.finding || '',
      remark: row.remark || '',
    }));
  } catch {
    return defaultSafetyCheckItems();
  }
}

export function createEmptySafetyPpeItem(): SafetyPpeItem {
  return {
    id: newId(),
    equipmentName: '',
    qty: '',
    issueDate: '',
    employeeName: '',
    departmentName: '',
    userId: null,
    departmentId: null,
    manualEntry: false,
    remark: '',
    signatureData: '',
  };
}

export function serializeSafetyPpeItems(rows: SafetyPpeItem[]): string {
  return JSON.stringify(
    rows.map((r) => {
      const manual =
        Boolean(r.manualEntry) ||
        (r.userId == null && Boolean((r.employeeName || '').trim()));
      return {
        id: r.id,
        equipmentName: r.equipmentName || '',
        qty: r.qty || '',
        issueDate: r.issueDate || '',
        employeeName: (r.employeeName || '').trim(),
        departmentName: (r.departmentName || '').trim(),
        userId: manual ? null : r.userId ?? null,
        departmentId: manual ? null : r.departmentId ?? null,
        manualEntry: manual,
        remark: r.remark || '',
        signatureData: r.signatureData || '',
      };
    }),
  );
}

export function createEmptySafetyCheckItem(): SafetyCheckItem {
  return {
    id: newId(),
    checkItem: '',
    method: '',
    result: '',
    finding: '',
    remark: '',
  };
}

export function safetyStatusLabel(status?: string | null) {
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
      return '승인완료';
    default:
      return '작성중';
  }
}
